import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Input, Select, Button, Badge, Dropdown, Avatar, message, Drawer, Menu, Space } from 'antd';
import { SearchOutlined, ShoppingCartOutlined, UserOutlined, MenuOutlined, LogoutOutlined, DashboardOutlined, CloseOutlined, RobotOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { useSearch } from '../../contexts/SearchContext';
import api from '../../services/api';
import soundNotificationService from '../../services/soundNotification';
// Removed ChatNotification to avoid duplicate with ChatPopover and bell
import NotificationCenter from '../UI/NotificationCenter';
import OrderStatusIndicator from '../UI/OrderStatusIndicator';
import AdvancedSearch from '../Search/AdvancedSearch';
import { useAISupport } from '../../contexts/AISupportContext';

function Header() {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [compact, setCompact] = useState(false);
    const navigate = useNavigate();
    const { user, logout, isAuthenticated } = useAuth();
    const { getCartItemCount, openCart } = useCart();
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [cities, setCities] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCityId, setSelectedCityId] = useState(() => localStorage.getItem('selected_city_id') || '');
    const [hideCitySelect, setHideCitySelect] = useState(false);
    const [brandLogo, setBrandLogo] = useState('/images/logo.svg');
    const [platformName, setPlatformName] = useState('Spoon & Spice');
    const [singleVendor, setSingleVendor] = useState(null);
    const { openAISupport } = useAISupport();

    useEffect(() => {
        const load = async () => {
            try {
                // Try cached values first (valid for 24h)
                const now = Date.now();
                const readCache = (key) => {
                    try { const raw = localStorage.getItem(key); if (!raw) return null; const obj = JSON.parse(raw); if (!obj || now - (obj.t || 0) > 24*60*60*1000) return null; return obj.v || null; } catch { return null; }
                };
                const writeCache = (key, v) => { try { localStorage.setItem(key, JSON.stringify({ t: now, v })); } catch {} };

                const cachedCities = readCache('cached_cities');
                const cachedCats = readCache('cached_categories');
                if (cachedCities) setCities(cachedCities);
                if (cachedCats) setCategories(cachedCats);

                console.log('Starting API calls...');
                const [citiesRes, catsRes, vendorsRes] = await Promise.allSettled([
                    api.getCities().catch(err => { console.error('Cities API error:', err); throw err; }),
                    api.getCategories().catch(err => { console.error('Categories API error:', err); throw err; }),
                    api.getVendors({ per_page: 2 }).catch(err => { console.error('Vendors API error:', err); throw err; })
                ]);
                console.log('API calls completed:', { citiesRes, catsRes, vendorsRes });
                const list = citiesRes.status === 'fulfilled' ? (citiesRes.value.data || []) : (cachedCities || []);
                setCities(list);
                if (citiesRes.status === 'fulfilled') writeCache('cached_cities', list);

                const cats = catsRes.status === 'fulfilled' ? (Array.isArray(catsRes.value.data) ? catsRes.value.data : []) : (cachedCats || []);
                setCategories(cats);
                if (catsRes.status === 'fulfilled') writeCache('cached_categories', cats);
                // Single vendor mode
                if (vendorsRes.status === 'fulfilled') {
                    console.log('Vendors API successful:', vendorsRes.value);
                    const listV = Array.isArray(vendorsRes.value.data?.data) ? vendorsRes.value.data.data : (vendorsRes.value.data || []);
                    if (Array.isArray(listV) && listV.length === 1) {
                        setSingleVendor(listV[0]);
                        if (listV[0]?.business_name) setPlatformName(listV[0].business_name);
                    }
                } else {
                    console.error('Vendors API failed:', vendorsRes.reason);
                }
                if (list.length === 1) {
                    setHideCitySelect(true);
                    const only = list[0];
                    setSelectedCityId(String(only.id));
                    localStorage.setItem('selected_city_id', String(only.id));
                }
            } catch (e) {
                console.error('Failed to load cities', e);
            }
        };
        load();
        // Unlock audio on first interaction for sounds
        try { soundNotificationService.resumeOnUserGestureOnce(); } catch(_) {}
    }, []);

    // Shrink-on-scroll header
    useEffect(() => {
        const onScroll = () => {
            setCompact(window.scrollY > 24);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => { window.removeEventListener('scroll', onScroll); };
    }, []);

    useEffect(() => {
        const loadBranding = async () => {
            try {
                const res = await api.getPublicSettings();
                const settings = res.data || {};
                
                console.log('Settings response:', settings); // Debug log
                
                // Handle logo - check both direct string and JSON format
                if (settings.settings_logo) {
                    console.log(settings, "settings")
                    let logoUrl = settings.settings_logo;
                    // If it's a JSON string, try to parse it
                    if (typeof logoUrl === 'string' && logoUrl.startsWith('{')) {
                        try {
                            const parsed = JSON.parse(logoUrl);
                            logoUrl = parsed.value || parsed.url || logoUrl;
                        } catch (e) {
                            // If parsing fails, use the string as is
                        }
                    }
                    // Validate logo URL before setting
                    if (logoUrl && (logoUrl.startsWith('/') || logoUrl.startsWith('http') || logoUrl.startsWith('data:'))) {
                        setBrandLogo(logoUrl);
                    }
                }
                
                // Handle platform name
                if (settings.settings_platform_name) {
                    let platformName = settings.settings_platform_name;
                    // If it's a JSON string, try to parse it
                    if (typeof platformName === 'string' && platformName.startsWith('{')) {
                        try {
                            const parsed = JSON.parse(platformName);
                            platformName = parsed.value || platformName;
                        } catch (e) {
                            // If parsing fails, use the string as is
                        }
                    }
                    setPlatformName(platformName);
                }
            } catch (e) {
                console.error('Failed to load branding settings:', e);
            }
        };
        loadBranding();
    }, []);

    const handleSelectCity = (id) => {
        setSelectedCityId(id);
        if (id) localStorage.setItem('selected_city_id', id); else localStorage.removeItem('selected_city_id');
    };

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const navigation = singleVendor ? [
        { name: 'Home', href: '/' },
        { name: 'Menu', href: '/menu' },
        { name: 'About', href: '/about' },
        { name: 'Contact', href: '/contact' },
    ] : [
        { name: 'Home', href: '/' },
        { name: 'About', href: '/about' },
        { name: 'Services', href: '/services' },
        { name: 'Menu', href: '/menu', mega: 'menu' },
        { name: 'Vendors', href: '/vendors', mega: 'vendors' },
        { name: 'Help', href: '/help-center' },
        { name: 'Contact', href: '/contact' },
    ];

    const selectedCityName = cities.find(c => String(c.id) === String(selectedCityId))?.name;

    const categoryIcon = (name = '') => {
        const n = name.toLowerCase();
        if (n.includes('biryani')) return '🍛';
        if (n.includes('tandoor')) return '🍢';
        if (n.includes('dessert')) return '🍰';
        if (n.includes('beverage')) return null;
        if (n.includes('snack') || n.includes('chaat')) return null;
        return null;
    };
    const cityIcon = (name = '') => {
        const n = name.toLowerCase();
        if (n.includes('mumbai')) return '🌆';
        if (n.includes('delhi')) return '🏛️';
        if (n.includes('bengaluru') || n.includes('bangalore')) return '🏙️';
        if (n.includes('kolkata')) return '🕍';
        if (n.includes('chennai')) return '🌴';
        return '📍';
    };

    return (<>
        <header className={`sticky top-0 z-50 header-modern transition-all duration-300 ${compact ? 'py-0 shadow-md' : 'py-0'}`}>
            {/* Top city bar - Elegant Design */}
            <div className="w-full bg-gradient-to-r from-[#FAF8F5] via-[#FAF8F5]/80 to-white text-slate-700 border-b border-gray-200/30">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3">
                    {!hideCitySelect && (
                        <>
                            <span className="text-sm font-semibold whitespace-nowrap flex items-center gap-2">
                                <span className="text-gray-600 text-lg">📍</span>
                                <span className="text-slate-700">Choose Your City:</span>
                            </span>
                            <Select
                                size="small"
                                className="min-w-[200px] search-modern"
                                value={selectedCityId || undefined}
                                placeholder="Select city"
                                allowClear
                                onChange={(v) => handleSelectCity(v || '')}
                                options={cities.map((c) => ({ 
                                    label: (
                                        <span className="flex items-center gap-2">
                                            <span>{cityIcon(c.name)}</span>
                                            <span>{c.name}</span>
                                        </span>
                                    ), 
                                    value: String(c.id) 
                                }))}
                                style={{ borderRadius: '8px', border: '1px solid rgba(229, 231, 235, 0.8)' }}
                            />
                        </>
                    )}
                    {!hideCitySelect && selectedCityName && (
                        <span className="ml-auto text-xs font-semibold hidden sm:flex items-center gap-1 text-slate-700">
                            <span>Viewing:</span>
                            <span className="text-gray-800 font-bold">{selectedCityName}</span>
                        </span>
                    )}
                </div>
            </div>
            <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className={`flex items-center justify-between gap-4 transition-all duration-300 ${compact ? 'h-14' : 'h-16'}`}>
                    <div className="flex items-center gap-4 flex-1">
                        <div className="flex flex-shrink-0 items-center">
                            <Link to="/" className="inline-flex items-center gap-3 hover:opacity-90 transition-all duration-200 group">
                                {brandLogo ? (
                                    <img 
                                        src={brandLogo} 
                                        alt={platformName} 
                                        className={`w-auto max-w-[200px] object-contain transition-all duration-300 group-hover:scale-105 ${compact ? 'h-8' : 'h-10'}`}
                                        onError={(e) => {
                                            console.error('Logo failed to load:', brandLogo);
                                            e.target.style.display = 'none';
                                            if (e.target.nextSibling) {
                                                e.target.nextSibling.style.display = 'block';
                                            }
                                            // Fallback to default logo
                                            if (brandLogo !== '/images/logo.svg') {
                                                setBrandLogo('/images/logo.svg');
                                            }
                                        }}
                                    />
                                ) : null}
                                <div 
                                    className={`flex items-center gap-2 ${brandLogo ? 'hidden' : 'flex'} ${compact ? 'text-lg' : 'text-xl'}`}
                                    style={{ display: brandLogo ? 'none' : 'flex' }}
                                >
                                    <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
                                        <span className="text-white font-bold text-sm">S&S</span>
                                    </div>
                                    <span className={`font-bold ${singleVendor ? 'text-rose-600' : 'text-indigo-600'} transition-all duration-200 group-hover:text-orange-600`}>
                                        {platformName}
                                    </span>
                                </div>
                            </Link>
                        </div>
                        <div className="hidden lg:block">
                            <Menu 
                                mode="horizontal" 
                                selectedKeys={[window.location.pathname]} 
                                items={navigation.map(n => ({ 
                                    key: n.href, 
                                    label: <Link to={n.href} className="hover:text-[#C97D60] transition-colors">{n.name}</Link> 
                                }))} 
                                style={{ 
                                    borderBottom: 'none', 
                                    background: 'transparent',
                                    fontSize: '15px',
                                    fontWeight: '500'
                                }} 
                                className="header-menu"
                            />
                        </div>
                    </div>
                    {/* Center search (desktop) - Modern Design */}
                    <div className="hidden lg:flex flex-1 max-w-md mx-4">
                        <div className="w-full search-modern">
                            <AdvancedSearch
                                onSearch={(q) => { if (q) navigate(`/search?q=${encodeURIComponent(q)}`); }}
                                placeholder="Search menus, vendors..."
                                compact={true}
                                showFilters={false}
                                showSuggestions={true}
                                showTrending={false}
                            />
                        </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-2">
                        <Badge count={getCartItemCount()} size="small" color="#ea580c" offset={[-2, 2]}>
                            <Button 
                                shape="circle" 
                                type="text" 
                                className="!h-10 !w-10 !p-0 flex items-center justify-center hover:bg-gray-50 transition-all duration-200"
                                onClick={openCart}
                                title="Cart"
                            >
                                <ShoppingCartOutlined className="h-5 w-5 text-slate-600 hover:text-[#C97D60] transition-colors" />
                            </Button>
                        </Badge>
                        {isAuthenticated && <NotificationCenter />}
                        {isAuthenticated && <OrderStatusIndicator />}
                        <Button
                            type="primary"
                            icon={<ThunderboltOutlined className="ai-icon-sparkle" />}
                            onClick={openAISupport}
                            className="text-white shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105"
                            style={{ 
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                border: 'none',
                                borderRadius: '10px',
                                height: '40px',
                                boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                            title="AI Support"
                        >
                            <span className="hidden md:inline">AI Support</span>
                        </Button>
                        <Link to="/get-started">
                            <Button 
                                type="primary" 
                                className="btn-brand btn-vibrant hidden md:inline-flex items-center gap-2"
                                style={{ height: '44px', borderRadius: '12px', padding: '0 24px' }}
                            >
                                Get Quote
                            </Button>
                        </Link>
                        {isAuthenticated ? (
                            <Dropdown
                                open={userMenuOpen}
                                onOpenChange={setUserMenuOpen}
                                menu={{
                                    items: [
                                        { key: 'dashboard', label: <Link to="/dashboard" onClick={() => setUserMenuOpen(false)}>Dashboard</Link> },
                                        { key: 'activity', label: <Link to="/activity" onClick={() => setUserMenuOpen(false)}>Activity</Link> },
                                        { key: 'settings', label: <Link to="/settings" onClick={() => setUserMenuOpen(false)}>Settings</Link> },
                                        { key: 'profile', label: <Link to="/profile" onClick={() => setUserMenuOpen(false)}>Profile</Link> },
                                        { key: 'chat', label: <Link to="/chat" onClick={() => setUserMenuOpen(false)}>My Chats</Link> },
                                        { key: 'quotes', label: <Link to="/quotes" onClick={() => setUserMenuOpen(false)}>My Quotes</Link> },
                                        ...(user?.user_type === 'vendor' ? [
                                            { key: 'v_dash', label: <Link to="/vendor/dashboard" onClick={() => setUserMenuOpen(false)}>Vendor Dashboard</Link> },
                                            { key: 'v_prof', label: <Link to="/vendor/profile" onClick={() => setUserMenuOpen(false)}>Vendor Profile</Link> },
                                            { key: 'v_menus', label: <Link to="/vendor/menus" onClick={() => setUserMenuOpen(false)}>Manage Menus</Link> },
                                            { key: 'v_leads', label: <Link to="/vendor/leads" onClick={() => setUserMenuOpen(false)}>Manage Leads</Link> },
                                            { key: 'v_bookings', label: <Link to="/vendor/bookings" onClick={() => setUserMenuOpen(false)}>Manage Bookings</Link> },
                                        ] : []),
                                        ...(user?.user_type === 'admin' ? [
                                            { key: 'a_dash', label: <Link to="/admin/dashboard" onClick={() => setUserMenuOpen(false)}>Admin Dashboard</Link> },
                                        ] : []),
                                        { type: 'divider' },
                                        { key: 'logout', label: <span onClick={handleLogout} className="text-red-600 font-semibold">Logout</span> },
                                    ],
                                }}
                            >
                                <button className="inline-flex items-center rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-md ring-1 ring-inset ring-gray-200 hover:bg-gray-50 transition-all duration-200 hover:shadow-lg">
                                    <Space size={6}>
                                        <Avatar size={28} style={{ backgroundColor: '#E5E7EB', color: '#1F2937', fontWeight: 'bold' }}>{(user?.name || 'U').slice(0,1).toUpperCase()}</Avatar>
                                        <span className="hidden md:inline font-semibold">{user?.name}</span>
                                    </Space>
                                </button>
                            </Dropdown>
                        ) : (
                            <>
                                <Link to="/login"><Button type="primary" size="middle" className="btn-vibrant" style={{ height: '40px', borderRadius: '10px' }}>Login</Button></Link>
                                <Link to="/register"><Button size="middle" style={{ height: '40px', borderRadius: '8px', border: '1px solid rgba(201, 125, 96, 0.3)', color: '#C97D60', fontWeight: '600' }}>Register</Button></Link>
                            </>
                        )}
                    </div>
                    <div className="-mr-2 flex items-center lg:hidden gap-2">
                        <Badge count={getCartItemCount()} size="small" color="#ea580c" offset={[-2, 2]} className="sm:hidden">
                            <Button 
                                shape="circle" 
                                type="text" 
                                className="!h-10 !w-10 !p-0 flex items-center justify-center"
                                onClick={openCart}
                                title="Cart"
                            >
                                <ShoppingCartOutlined className="h-5 w-5 text-slate-600" />
                            </Button>
                        </Badge>
                        <Button 
                            type="text" 
                            aria-label="Open main menu" 
                            onClick={() => setIsMenuOpen(true)} 
                            className="flex items-center !h-10 !w-10 !p-0 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            <Bars3Icon className="h-6 w-6 text-slate-600" />
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Mobile Drawer */}
            <Drawer
                placement="right"
                width={320}
                open={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                bodyStyle={{ padding: 16 }}
            >
                <div className="space-y-4">
                    <div>
                        <AdvancedSearch
                            onSearch={(q) => { setIsMenuOpen(false); if (q) navigate(`/search?q=${encodeURIComponent(q)}`); }}
                            placeholder="Search menus, vendors..."
                            compact={true}
                            showFilters={false}
                            showSuggestions={true}
                            showTrending={false}
                        />
                    </div>
                    {/* {!hideCitySelect && (
                        <div>
                            <div className="text-xs text-slate-500 mb-1">Choose Your City</div>
                            <Select
                                className="w-full"
                                value={selectedCityId || undefined}
                                placeholder="Select city"
                                allowClear
                                onChange={(v) => handleSelectCity(v || '')}
                                options={cities.map((c) => ({ label: c.name, value: String(c.id) }))}
                            />
                        </div>
                    )} */}
                    <Menu
                        mode="inline"
                        selectedKeys={[window.location.pathname]}
                        items={navigation.map(n => ({ key: n.href, label: <Link to={n.href} onClick={() => setIsMenuOpen(false)}>{n.name}</Link> }))}
                        style={{ borderRight: 'none' }}
                    />
                    <div className="pt-2 border-t border-slate-200">
                        {/* AI Support Button - Always visible */}
                        <div className="mb-4">
                            <Button
                                type="primary"
                                icon={<ThunderboltOutlined className="ai-icon-sparkle" />}
                                onClick={() => { openAISupport(); setIsMenuOpen(false); }}
                                className="w-full text-white shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105"
                                style={{ 
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    border: 'none',
                                    borderRadius: '8px',
                                    height: '44px',
                                    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}
                            >
                                AI Support
                            </Button>
                        </div>
                        
                        {isAuthenticated ? (
                            <div className="space-y-2">
                                <div className="text-sm text-slate-600">Welcome, {user?.name}</div>
                                <div className="flex items-center justify-between">
                                    <span className="text-slate-700">Notifications</span>
                                    <NotificationCenter />
                                </div>
                                <Link to="/dashboard" onClick={() => setIsMenuOpen(false)} className="block text-slate-700">Dashboard</Link>
                                <Link to="/activity" onClick={() => setIsMenuOpen(false)} className="block text-slate-700">Activity</Link>
                                <Link to="/settings" onClick={() => setIsMenuOpen(false)} className="block text-slate-700">Settings</Link>
                                <Link to="/quotes" onClick={() => setIsMenuOpen(false)} className="block text-slate-700">My Quotes</Link>
                                    {user?.user_type === 'vendor' && (
                                    <>
                                        <Link to="/vendor/dashboard" onClick={() => setIsMenuOpen(false)} className="block text-slate-700">Vendor Panel</Link>
                                        <Link to="/vendor/menus" onClick={() => setIsMenuOpen(false)} className="block text-slate-700">Manage Menus</Link>
                                </>
                            )}
                                {user?.user_type === 'admin' && (
                                    <Link to="/admin/dashboard" onClick={() => setIsMenuOpen(false)} className="block text-slate-700">Admin Dashboard</Link>
                                )}
                                <Button danger className="mt-2" onClick={() => { handleLogout(); setIsMenuOpen(false); }}>Logout</Button>
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                <Link to="/login" onClick={() => setIsMenuOpen(false)}><Button type="primary" block>Login</Button></Link>
                                <Link to="/register" onClick={() => setIsMenuOpen(false)}><Button block>Register</Button></Link>
                        </div>
                        )}
                    </div>
                </div>
            </Drawer>
            
        </header>
        {/* Mega-menu removed per request; simple menu retained. */}
    </>
    );
}

export default Header;
